import React from "react";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaEuroSign,
  FaGift,
  FaReceipt,
} from "react-icons/fa";

import { formatDateFR, formatEuro } from "../utils/subscriptionDashboardUtils";

function KpiCard({ label, value, detail, icon, loading, tone = "base" }) {
  const tones = {
    base: "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900",
    info: "border-indigo-200 bg-indigo-50 dark:border-indigo-500/20 dark:bg-indigo-500/10",
    ok: "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10",
    warn: "border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10",
    danger: "border-rose-200 bg-rose-50 dark:border-rose-500/20 dark:bg-rose-500/10",
    violet:
      "border-violet-200 bg-violet-50 dark:border-violet-500/20 dark:bg-violet-500/10",
  };

  return (
    <article
      className={`rounded-[24px] border p-4 shadow-sm ${
        tones[tone] || tones.base
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            {label}
          </p>

          {loading ? (
            <div className="mt-3 h-7 w-24 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
          ) : (
            <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
              {value}
            </p>
          )}

          {detail ? (
            <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
              {detail}
            </p>
          ) : null}
        </div>

        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:text-indigo-300 dark:ring-slate-700">
          {icon}
        </div>
      </div>
    </article>
  );
}

export default function SubscriptionKpiCards({ stats, loading }) {
  const remaining =
    typeof stats.closestDaysRemaining === "number"
      ? `J-${stats.closestDaysRemaining}`
      : "—";

  return (
    <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
      <KpiCard
        label="Abonnements"
        value={stats.subscriptionCount}
        detail={`${stats.activeCount} actif(s) • ${stats.expiredCount} terminé(s)`}
        icon={<FaReceipt />}
        loading={loading}
        tone="info"
      />

      <KpiCard
        label="Statut"
        value={stats.activeCount > 0 ? "Actif" : "À vérifier"}
        detail="État actuel du compte club"
        icon={<FaCheckCircle />}
        loading={loading}
        tone={stats.activeCount > 0 ? "ok" : "warn"}
      />

      <KpiCard
        label="Montant abonnements"
        value={formatEuro(stats.subscriptionAmount)}
        detail="Total des abonnements hors parrainage"
        icon={<FaEuroSign />}
        loading={loading}
        tone="base"
      />

      <KpiCard
        label="Parrainages"
        value={formatEuro(stats.referralAmount)}
        detail={`${stats.referralCount} réduction(s) enregistrée(s)`}
        icon={<FaGift />}
        loading={loading}
        tone={stats.referralCount ? "violet" : "base"}
      />

      <KpiCard
        label="Prochaine échéance"
        value={remaining}
        detail={
          stats.closestEndDate
            ? `Fin le ${formatDateFR(stats.closestEndDate)}`
            : "Aucune échéance active"
        }
        icon={<FaClock />}
        loading={loading}
        tone={
          typeof stats.closestDaysRemaining === "number" &&
          stats.closestDaysRemaining <= 30
            ? "warn"
            : "base"
        }
      />

      <KpiCard
        label="Net affiché"
        value={formatEuro(stats.netAmount)}
        detail="Abonnements + réductions"
        icon={<FaCalendarAlt />}
        loading={loading}
        tone="info"
      />
    </section>
  );
}